<template>
  <div>
    <LoadingOverlay v-if="isLoading" />
    <b-container
      v-else
      :fluid="true"
    >
      <b-row>
        <b-col cols="10">
          <h1>Courts</h1>
        </b-col>
        <b-col class="center">
          <button
            class="custom-button blue rounded uk-margin-remove"
            @click="showAddCourtModal"
          >
            Add a Court
          </button>
        </b-col>
      </b-row>
    </b-container>
    <hr>
    <div v-if="courts.length">
      <ul>
        <li
          v-for="court in courts"
          :key="court.id"
          class="list-item d-flex align-items-stretch"
        >
          <div class="d-flex flex-row align-self-center p-2 w-100">
            <div class="d-flex mr-auto p-2 list-item-title">
              <span class="align-self-center">{{ court.name }} at {{ court.location }}</span>
            </div>
            <div
              class="d-flex m-2 align-content-center"
            >
              <b-button
                variant="primary"
                @click="showEditCourt(court.id)"
              >
                Edit
              </b-button>
            </div>
            <div
              class="d-flex m-2 align-content-center text-center"
            >
              <b-button
                variant="danger"
                @click="showDeleteCourtModal(court.id)"
              >
                Delete
              </b-button>
            </div>
          </div>
        </li>
      </ul>
    </div>
    <div v-else>
      {{ emptyMessage }}
    </div>
    <ModalOverlay
      id="editCourtModal"
      ref="editCourtModal"
      modal-id="editCourtModal"
      :modal-show="showModal"
      :title="`Edit ${courtBeingEdited?courtBeingEdited.name:''}`"
      :hide-footer="true"
      size="lg"
      @hide="editCourtClosed"
    >
      <div class="d-block">
        <b-form
          v-if="courtBeingEdited && courtDetailsToBeUpdated"
          @submit="submitEditCourt"
        >
          <b-form-group
            id="editCourtName"
            label="Court Name:"
            label-for="editCourtNameInput"
          >
            <b-form-input
              id="editCourtNameInput"
              v-model="courtDetailsToBeUpdated.name"
              type="text"
              required
              placeholder="Enter court name"
            />
          </b-form-group>
          <b-form-group
            id="editCourtLocation"
            label="Court Location:"
            label-for="editCourtLocationInput"
          >
            <b-form-select
              v-model="courtDetailsToBeUpdated.locationId"
              :options="locationSelectOptions"
            />
          </b-form-group>
          <div class="text-center">
            <b-button
              type="submit"
              variant="primary"
            >
              Update Details
            </b-button><br>
          </div>
        </b-form>
      </div>
    </ModalOverlay>
    <ModalOverlay
      id="addCourtModal"
      ref="addCourtModal"
      modal-id="addCourtModal"
      :modal-show="showAddCourt"
      :title="`Add New Court`"
      size="lg"
      ok-title="Add Court"
      @hide="createCourtClosed"
      @confirmed="submitAddCourt"
    >
      <div class="d-block">
        <b-form
          v-if="showAddCourt"
          @submit.prevent="submitAddCourt"
        >
          <b-form-group
            id="createCourtName"
            label="Court Name:"
            label-for="createCourtNameInput"
          >
            <b-form-input
              id="createCourtNameInput"
              v-model="newCourt.name"
              type="text"
              required
              placeholder="Enter court name"
            />
          </b-form-group>
          <b-form-group
            id="createCourtLocation"
            label="Court Location:"
            label-for="createCourtLocationInput"
          >
            <b-form-select
              v-model="newCourt.locationId"
              :options="locationSelectOptions"
            />
          </b-form-group>
        </b-form>
      </div>
    </ModalOverlay>
    <ModalOverlay
      id="deleteCourtModal"
      ref="deleteCourtModal"
      modal-id="deleteCourtModal"
      :modal-show="showDeleteCourtPrompt"
      size="md"
      title="Delete Confirmation"
      ok-title="Delete"
      ok-variant="danger"
      @hide="editCourtClosed"
      @confirmed="submitDeleteCourt"
    >
      <div
        v-if="courtBeingEdited"
        class="d-block"
        style=""
      >
        <span>
          <b-icon-exclamation-triangle-fill />
          Deleting {{ courtBeingEdited.name }} will result in any reservations for this court being cancelled.
        </span>
        <div class="text-center p-t-10">
          <span class="font-weight-bold">Do you wish to continue?</span>
        </div>
      </div>
    </ModalOverlay>
  </div>
</template>

<script>
import axios from 'axios';
import clone from 'lodash/clone';
import find from 'lodash/find';
import merge from 'lodash/merge';
import LoadingOverlay from '../../common/LoadingOverlay';
import ModalOverlay from '../../common/ModalOverlay';
import { emitToast, VARIANT_TYPE } from '../../../util/Toast';
import { getStatusFromResponseIfExists, HTTP_STATUS_CODE } from '../../../util';
import { mapState } from 'vuex';

export default {
  name: 'Courts',
  components: { LoadingOverlay, ModalOverlay },
  data: () => ({
    courtBeingEdited:null,
    courtDetailsToBeUpdated:null,
    courts: '',
    emptyMessage: 'Loading Courts...',
    isLoading:true,
    locationSelectOptions: null,
    locations:'',
    newCourt: {},
    defaultLocationId: null,

    showAddCourt: false,
    showDeleteCourtPrompt: false,
    showModal:false,
  }),
  computed: {
    ...mapState({
      organization: state => state.organization,
    }),
  },
  created() {
    this.getData();
  },
  methods: {

    createCourtClosed() {
      this.newCourt = {
        locationId: this.defaultLocationId,
      };
    },

    editCourtClosed() {
      this.courtBeingEdited = null;
      this.courtDetailsToBeUpdated = null;
    },

    async getData() {
      const [{ data: courts }, { data: { locations } }] = await Promise.all([
        axios.get(`/api/${this.organization.slug}/court/all`),
        axios.get(`/api/${this.organization.slug}/location`),
      ]);
      this.locationSelectOptions = locations.map(location => {
        if (location.isDefault) this.defaultLocationId = location.id;
        return {
          text: location.address,
          value: location.id,
        };
      });
      this.courts = courts;
      this.locations = locations;
      this.emptyMessage = 'There are no courts';
      this.isLoading = false;
      this.newCourt.locationId = this.defaultLocationId
    },

    showAddCourtModal(){
      this.showAddCourt = true;
      this.$refs.addCourtModal.show();
    },

    showDeleteCourtModal(courtId) {
      this.courtBeingEdited = find(this.courts,['id',courtId]);
      if (this.courtBeingEdited)
        this.$refs.deleteCourtModal.show();
    },

    showEditCourt(courtId) {
      this.courtBeingEdited = find(this.courts,['id',courtId]);
      this.courtDetailsToBeUpdated = clone(this.courtBeingEdited);
      this.courtBeingEdited = find(this.courts,['id',courtId]);
      if (this.courtBeingEdited){
        this.courtDetailsToBeUpdated.locationId = find(this.locations, ['id', this.courtBeingEdited.locationId]).id;
        this.$refs['editCourtModal'].show();
      }
    },

    async submitAddCourt() {
      const { name, locationId } = this.newCourt;
      const { data: { id } } = await axios.post(`/api/${this.organization.slug}/manage/court`,{
        locationId,
        name,
      });

      const newCourtLocation = find(this.locations, ['id', locationId]);

      this.courts.push({
        id,
        location: newCourtLocation.address,
        locationId: newCourtLocation.id,
        name,
      });
    },

    async submitDeleteCourt(){
      try {
        const courtId = this.courtBeingEdited.id;
        this.isLoading = true;
        await axios.post(`/api/${this.organization.slug}/manage/court/delete`, {
          courtId,
        });
        this.courts = this.courts.filter(court => court.id !== courtId);
      } catch (e){
        // Ignore
      }
      this.isLoading = false;
    },

    async submitEditCourt(event) {
      event.preventDefault();
      const { id,name,locationId } = this.courtDetailsToBeUpdated;
      this.isLoading = true;
      try {
        await axios.post(`/api/${this.organization.slug}/manage/court/update`, {
          courtId: id,
          courtName: name,
          locationId: locationId,
        });
        const newLocation = find(this.locations, ['id', locationId]);
        merge(find(this.courts, ['id', id]), {
          location: newLocation.address,
          locationId,
          name,
        });
        emitToast(this.$bvToast, VARIANT_TYPE.SUCCESS, 'The court has been updated!');
      } catch (e) {
        const { response } = e;
        let errorMessage;
        switch (getStatusFromResponseIfExists(response)) {
          case HTTP_STATUS_CODE.FORBIDDEN:
            errorMessage = 'You do not have permission to edit courts';
            break;
          default: errorMessage = 'An unknown error occurred.';
        }
        emitToast(this.$bvToast, VARIANT_TYPE.WARNING, errorMessage);
      }
      this.$refs['editCourtModal'].hide();
      this.isLoading = false;
    },
  },
};
</script>

<style scoped>

</style>
